
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false);
    window.REACT_APP_NAVIGATE(path);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 py-4 md:py-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="https://static.readdy.ai/image/6b6b6fa7c6c47b49bd5802c74709554d/b8dabc6cce1111c1baf649ac8ec32ef9.png" 
              alt="Superior Equipment and Technology Logo" 
              className="h-8 md:h-10 w-auto object-contain drop-shadow-sm mix-blend-multiply"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' }}
            />
            <span className={`text-sm md:text-xl font-bold transition-colors ${isScrolled ? 'text-[#0D1B3E]' : 'text-white'}`}>
              Superior Equipment & Technology
            </span>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-10">
            <button
              onClick={() => scrollToSection('overview')}
              className={`text-base font-medium transition-colors hover:text-[#0066FF] whitespace-nowrap ${
                isScrolled ? 'text-[#0D1B3E]' : 'text-white'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className={`text-base font-medium transition-colors hover:text-[#0066FF] whitespace-nowrap ${
                isScrolled ? 'text-[#0D1B3E]' : 'text-white'
              }`}
            >
              Solutions
            </button>
            <a
              href="/distributors"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/distributors');
              }}
              className={`text-base font-medium transition-colors hover:text-[#0066FF] whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-[#0D1B3E]' : 'text-white'
              }`}
            >
              Partners
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-base font-medium transition-colors hover:text-[#0066FF] whitespace-nowrap ${
                isScrolled ? 'text-[#0D1B3E]' : 'text-white'
              }`}
            >
              Contact
            </button>
          </div>

          {/* Desktop CTA Button */}
          <a 
            href="/quote"
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/quote');
            }}
            className="hidden lg:block px-6 md:px-8 py-2 md:py-3 bg-gradient-to-r from-[#0D1B3E] to-[#0066FF] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap text-sm md:text-base"
          >
            Request Quote
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${
              isScrolled ? 'text-[#0D1B3E]' : 'text-white'
            }`}
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200">
            <div className="flex flex-col py-4">
              <button
                onClick={() => scrollToSection('overview')}
                className="px-6 py-3 text-left text-base font-medium text-[#0D1B3E] hover:bg-gray-50 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="px-6 py-3 text-left text-base font-medium text-[#0D1B3E] hover:bg-gray-50 transition-colors"
              >
                Solutions
              </button>
              <button
                onClick={() => handleNavigation('/distributors')}
                className="px-6 py-3 text-left text-base font-medium text-[#0D1B3E] hover:bg-gray-50 transition-colors"
              >
                Partners
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-3 text-left text-base font-medium text-[#0D1B3E] hover:bg-gray-50 transition-colors"
              >
                Contact
              </button>
              <div className="px-6 py-3">
                <button
                  onClick={() => handleNavigation('/quote')}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#0D1B3E] to-[#0066FF] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                >
                  Request Quote
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
